/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import FormQuantityAdd from 'layouts/purchaserequest/FormQuantityAdd';

const initFind = {
  search: '',
  order_by: 'pd.barcode',
  order_sort: 'asc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

function SearchProduct({ add }) {
  const mod = 'search_product';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalAdd, setModalAdd] = useState(false);
  const txtSearch = useRef(null);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'pd.image_id', label: t(`attr.image`), align: 'center' },
    { id: 'pd.barcode', label: 'Barcode', align: 'left' },
    { id: 'pd.product_name', label: 'สินค้า', align: 'left' },
    { id: 'add', label: 'จำนวนต้องการ', align: 'center', sort: false },
    { id: 'pd.unit_id', label: 'หน่วย', align: 'left' },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const addCallback = (item, quantity) => {
    add({
      barcode_id: item.id,
      quantity: quantity,
      unit_id: item.unit_id,
    });
  };

  const renderSearch = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  inputRef: txtSearch,
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0, minHeight: 600 }}>
              <Table aria-label='simple table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell width='40'>{Func.imageDisplay(row)}</TableCell>
                      <TableCell>{row.barcode} </TableCell>
                      <TableCell>
                        <MDBox>{row.code}</MDBox>
                        <MDBox>{row.name}</MDBox>
                      </TableCell>
                      <TableCell align='center' width={150}>
                        <FormQuantityAdd parent={row} callback={addCallback} />
                      </TableCell>
                      <TableCell align='left' width={90}>
                        {row.unit_name}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
              <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                {t(`element.show_record`, { num: find.perPage })}&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              {renderShow}
              <Pagination
                count={pageTotal}
                variant='outlined'
                color='secondary'
                showFirstButton
                showLastButton
                page={pageNo}
                onChange={(e, p) => view({ ...find, page: p })}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog
        open={modalAdd}
        maxWidth='lg'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => setModalAdd(false)}
      >
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderList()}</DialogContent>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    view(find);
  }, [id]);

  return (
    <div>
      <MDButton
        variant='outlined'
        color='info'
        onClick={() => {
          setModalAdd(true);
          view(find);

          setTimeout(function () {
            txtSearch.current.focus();
          }, 500);
        }}
      >
        <Icon>add_circle_outline</Icon>&nbsp;เพิ่มสินค้า
      </MDButton>

      {renderAdd()}
      {renderLoading()}
      {notify}
    </div>
  );
}

SearchProduct.propTypes = {
  add: PropTypes.func.isRequired,
};

export default SearchProduct;
